import { groupBy, uniq } from "lodash";
import { applySnapshot, getSnapshot, types } from "mobx-state-tree";
import { IdEntity } from "modules/common/models/entity";
import { flow } from "modules/common/models/flow";
import { Notificator } from "modules/common/models/notificator";
import { Queryable } from "modules/common/models/queryable";
import { Transport } from "modules/common/models/transport";
import { apiUrls } from "modules/common/services/communication/urls";
import { texts } from "modules/common/texts";
import { Constants } from "modules/root/models/constants";

export const RoleAccessRecord = types
  .model({
    name: types.string,
    value: types.boolean,
    overwritten: types.boolean,
  })
  .named("RoleAccessRecord");

export const RoleAccess = types
  .compose(
    Transport,
    Notificator,
    IdEntity,
    types.model({
      name: types.string,
      access: types.array(RoleAccessRecord),
      department: types.string,
    })
  )
  .views((self) => ({
    get label() {
      return self.name;
    },

    get sublabel() {
      return self.department;
    },

    get functionalities() {
      return self.access.filter((a) => a.value).map((a) => a.name);
    },
  }))
  .actions((self) => ({
    setWholeAccess: flow(function* (access: string[]) {
      try {
        const data: any = yield self.transport.post<any>(apiUrls.employeePositions.wholeAccess(self.id), {
          access,
        });

        applySnapshot(self, data);

        self.notify.success(texts.messages.saved);
        return true;
      } catch (er) {
        self.notify.error(er);
        return false;
      }
    }),

    setFunctionalityAccess: flow(function* (functionality: string, state: boolean) {
      try {
        const data: any = yield self.transport.post<any>(apiUrls.employeePositions.singleAccess(self.id), {
          functionality,
          state,
        });

        applySnapshot(self, data);

        return true;
      } catch (er) {
        self.notify.error(er);
        return false;
      }
    }),
  }))
  .named("RoleAccess");

export const AccessStore = types
  .compose(
    Transport,
    Notificator,
    Queryable,
    types.model({
      roles: types.array(RoleAccess),
      authorization: types.map(types.string),
    })
  )
  .views((self) => ({
    get filteredRoles() {
      const data = self.roles.map((role) => ({
        ...getSnapshot(role),
        label: role.label,
        sublabel: role.sublabel,
      }));

      return self.pureQuery
        ? data.filter((role) => {
            const text = `${role.name} ${role.department}`.toLowerCase();
            return text.indexOf(self.pureQuery) >= 0;
          })
        : data;
    },
  }))
  .views((self) => ({
    get departments() {
      return uniq(self.filteredRoles.map((r) => r.department));
    },
  }))
  .actions((self) => ({
    load: flow(function* () {
      applySnapshot(self.authorization, getSnapshot(Constants.authorization));

      try {
        const data = yield self.transport.get<RoleAccessSnapshotType[]>(apiUrls.employee.access);

        applySnapshot(self.roles, data);

        return true;
      } catch (er) {
        self.notify.error(er);
        return false;
      }
    }),
  }))
  .views((self) => ({
    get accessTree() {
      return buildAccessTree(self.authorization.entries(), Array.from(self.authorization));
    },
  }))
  .views((self) => ({
    get accessColumns() {
      const res = makeColumns(self.accessTree, 1);
      return res;
    },
  }))
  .named("AccessStore");

export type RoleAccessRecordSnapshotType = typeof RoleAccessRecord.SnapshotType;
export type RoleAccessSnapshotType = typeof RoleAccess.SnapshotType;
export type RoleAccessType = typeof RoleAccess.Type;
export type AccessStoreSnapshotType = typeof AccessStore.SnapshotType;
export type AccessStoreType = typeof AccessStore.Type;

export const initialState = (): AccessStoreSnapshotType => ({
  roles: [],
  authorization: {},
  pureQuery: "",
  query: "",
});

export const buildRows = (data: [string, string][]) => {
  let level1: string[] = [];
  const result: TreeRow[] = data.map((functionality) => {
    const [name, label] = functionality;
    const parts = makeLabels(label);

    if (parts[0].trim()) {
      level1.push(parts[0]);
    }

    return {
      functionality: name,
      level1: parts[0],
      level2: parts[1],
      level3: parts[2],
    };
  });

  // normalize columns
  level1 = uniq(level1);
  result.forEach((row) => {
    if (!row.level1 && level1.includes(row.level2) && row.level3) {
      row.level1 = row.level2;
      row.level2 = row.level3;
      row.level3 = "";
    }
  });

  return result;
};

const makeLabels = (label: string) => {
  const parts = label.split(".").filter((part) => !!part);
  const result = ["", "", ""];

  if (parts.length === 2) {
    result[2] = parts[1];
    result[1] = parts[0];
  } else if (parts.length === 3) {
    result[2] = parts[2];
    result[1] = parts[1];
    result[0] = parts[0];
  } else if (parts.length === 4) {
    result[2] = parts[2] + " " + parts[3];
    result[1] = parts[1];
    result[0] = parts[0];
  } else {
    result[2] = label;
  }

  return result;
};

export interface TreeRow {
  level1: string;
  level2: string;
  level3: string;
  functionality: string;
}

export interface AccessRolesModule extends TCounted {
  module: string;
  tree: TreeRow[];
}

export interface RoleData {
  id: string;
  label: string;
  sublabel: string;
  access: RoleAccessRecordSnapshotType[];
}

interface TCounted {
  length: () => number;
}

/**
 * Разбить набор коллекций на колонки ~ равной величины
 * @param array Набор коллекций
 * @param columnCount Кол-во колонок
 */
export function makeColumns<T extends TCounted>(array: T[], columnCount: number) {
  if (columnCount <= 1) return [array.slice()]; // Если одна колонка - вернуть копию массива

  // Вычисляем общее количество элементов
  const totalLength = array.reduce((sum, part) => sum + part.length(), 0);
  // Вычисляем целевой размер каждой колонки
  const chunkLength = Math.ceil(totalLength / columnCount);

  const portions: T[][] = [];
  let currentPortion: T[] = [];
  let currentLength = 0;

  for (const item of array) {
    currentPortion.push(item);
    currentLength += item.length();

    // Если текущая порция достигла или превысила целевой размер
    if (currentLength >= chunkLength) {
      portions.push(currentPortion);
      currentPortion = [];
      currentLength = 0;
    }
  }

  // Добавляем оставшиеся элементы, если они есть
  if (currentPortion.length > 0) {
    portions.push(currentPortion);
  }

  // Если получилось меньше колонок, чем нужно, добавляем пустые
  while (portions.length < columnCount) {
    portions.push([]);
  }

  return portions;
}

/**
 * Собрать древовидное представление из списка функциональностей
 * @param authorization Полный список функциональностей приложения [ключ, название]
 */
export function buildAccessTree(authorization: IterableIterator<[string, string]>, data?: [string, string][]) {
  const modules = groupBy(Array.from(authorization), (e) => e[1].split(".")[0]);

  // Получаем уникальные модули из data в порядке их появления
  const uniqueModules: string[] = [];
  const seenModules = new Set<string>();
  if (data)
    for (const item of data) {
      const moduleName = item[1].split(".")[0].trim();
      if (!seenModules.has(moduleName)) {
        seenModules.add(moduleName);
        uniqueModules.push(moduleName);
      }
    }

  // Сортируем модули согласно порядку в uniqueModules
  var res = uniqueModules
    .filter((moduleName) => modules[moduleName]) // Оставляем только те, что есть в modules
    .map((moduleName) => {
      const moduleData = modules[moduleName];
      return {
        module: moduleName,
        tree: buildRows(moduleData),
        length: () => moduleData.length,
      } as AccessRolesModule;
    });

  return [...res];
}
